/**
 * PasswordStrengthMeter — visual strength indicator + rules checklist.
 * Pure presentational; consumer passes `password` + optional `email`.
 * Polls /api/auth/password-check on debounced input change.
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Check, X, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR_MAP = {
  rose:    'bg-rose-500',
  orange:  'bg-orange-500',
  amber:   'bg-amber-500',
  emerald: 'bg-emerald-500',
  gold:    'bg-gold',
  gray:    'bg-gray-600',
};

const RULES_HI = [
  { test: (p) => p.length >= 10,             label: 'कम-से-कम 10 अक्षर' },
  { test: (p) => /[A-Z]/.test(p),            label: '1 बड़ा अक्षर (A-Z)' },
  { test: (p) => /[a-z]/.test(p),            label: '1 छोटा अक्षर (a-z)' },
  { test: (p) => /[0-9]/.test(p),            label: '1 अंक (0-9)' },
  { test: (p) => /[!@#$%^&*()_+\-=[\]{}|;:,.<>?/~`'"]/.test(p), label: '1 विशेष चिह्न (!@#$ आदि)' },
];

export default function PasswordStrengthMeter({ password = '', email = '' }) {
  const [meta, setMeta] = useState({ score: 0, label_hi: '—', color: 'gray' });
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!password) {
      setMeta({ score: 0, label_hi: '—', color: 'gray' });
      setErrors([]);
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.post(`${API}/api/auth/password-check`, { password, email });
        setMeta(data.strength || { score: 0, label_hi: '—', color: 'gray' });
        setErrors(data.errors_hi || []);
      } catch (e) {
        setMeta({ score: 0, label_hi: '—', color: 'gray' });
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [password, email]);

  const colorCls = COLOR_MAP[meta.color] || COLOR_MAP.gray;

  return (
    <div className="space-y-2 mt-2" data-testid="password-strength-meter">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
          <div
            className={`h-full ${colorCls} transition-all duration-500 ease-out`}
            style={{ width: `${meta.score}%` }}
            data-testid="strength-bar"
          />
        </div>
        <span className="text-xs font-semibold text-text-muted min-w-[80px] text-right" data-testid="strength-label">
          {loading ? <Loader2 className="w-3 h-3 inline animate-spin" /> : meta.label_hi}
        </span>
      </div>

      {/* Rule checklist */}
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        {RULES_HI.map((r, i) => {
          const ok = r.test(password);
          return (
            <li key={i} className={`flex items-center gap-1.5 ${ok ? 'text-emerald-400' : 'text-text-muted/70'}`}>
              {ok ? <Check className="w-3 h-3 flex-shrink-0" /> : <X className="w-3 h-3 flex-shrink-0" />}
              {r.label}
            </li>
          );
        })}
      </ul>

      {errors.length > 0 && password && (
        <div className="text-[11px] text-rose-300 mt-1 space-y-0.5" data-testid="strength-errors">
          {errors.slice(0, 3).map((e, i) => <div key={i}>⚠ {e}</div>)}
        </div>
      )}
    </div>
  );
}
